import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useContactsCached } from '../hooks/useContactsCached';
import useHubNotifications from '../hooks/useHubNotifications';
import { requestIdle } from '../utils/idleCallback';
import styles from '../components/home/HomeHub.module.css';
import {
  HomeTopBar,
  HomeSidebar,
  KpiCard,
  ModuleCard,
  EmptyState,
  LastSavedCardStack,
  CardStackSkeleton,
  ModuleSkeletonList,
  ModuleSkeletonNews,
} from '../components/home/HomeHubParts';
import NotificationsDrawer from '../components/home/NotificationsDrawer';
import GuestMergeNoticeBanner from '../components/GuestMergeNoticeBanner';
import { useAccount } from '../contexts/AccountContext';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { trackNewsV2Event } from '../api/newsV2';
import { getNewsHeadlineTag } from '../utils/newsHeadlineTag';
import { hasNewsPreferences, hasEventPreferences } from '../utils/insightPreferences';
import { isBriefingInProgress, selectHomeTeasers, selectUpcomingEvents } from '../utils/homeInsightsPreview';
import { refreshCurrentMarketIfNeeded } from '../utils/marketLocation';

function formatLongDate(date) {
  // Example: "Tuesday, Feb 3, 2026" (uses the user's locale)
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

const contactSortTs = (c) => {
    const v = c?.connectedAt || c?.createdAt || c?.updatedAt || 0;
    const t = typeof v === 'number' ? v : Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  };

function getMostRecentContacts(contacts, max = 3) {
  if (!Array.isArray(contacts) || contacts.length === 0) return [];
  const sorted = contacts
  .slice()
  .sort((a, b) => {
    const tb = contactSortTs(b);
    const ta = contactSortTs(a);
    if (tb !== ta) return tb - ta;
    // Stable-ish deterministic tie-breaker (never promotes timestamp-missing items)
    const kb = String(b?._id || b?.email || b?.name || '');
    const ka = String(a?._id || a?.email || a?.name || '');
    return kb.localeCompare(ka);
  });
return sorted.slice(0, max);
}

function isAbortError(err) {
  return (
    err?.name === 'AbortError' ||
    err?.name === 'CanceledError' ||
    err?.code === 'ERR_CANCELED' ||
    err?.message === 'canceled' ||
    err?.message === 'canceled by user'
  );
}

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const todayLabel = formatLongDate(new Date());

  const [isNavOpen, setIsNavOpen] = useState(false);

  // Shared with the shell and the account hub rather than fetched again here.
  const { account: userData, isLoading: isLoadingAccount } = useAccount();
  const { has, isLoading: entitlementsLoading } = useEntitlements();
  const canSeeEvents = has('insights_events');
  const canSeeBriefing = has('insights_full');
  const [preferences, setPreferences] = useState(null);
  const [watchlistCount, setWatchlistCount] = useState(0);
  const [engagementSummary, setEngagementSummary] = useState(null);
  const [engagementRefreshKey, setEngagementRefreshKey] = useState(0);

  const [eventsExtract, setEventsExtract] = useState(null);
  /** @type {Array<{veloxHeadline?: string, oneLiner?: string, kind?: string, industry?: string, linkTarget?: string, topic?: string, technology?: string}> | null} */
  const [newsExtract, setNewsExtract] = useState(null);
  const [newsTeaserStatus, setNewsTeaserStatus] = useState(null);
  /** @type {{ isStale?: boolean, refreshPending?: boolean, lastCorpusRefreshAt?: string } | null} */
  const [newsMeta, setNewsMeta] = useState(null);
  const [eventsLocked, setEventsLocked] = useState(false);

  const [isLoadingPrefs, setIsLoadingPrefs] = useState(true);
  const [isLoadingEngagement, setIsLoadingEngagement] = useState(true);
  const [isLoadingExtracts, setIsLoadingExtracts] = useState(false);

  const { contacts, isLoading: isLoadingContacts, refreshContacts } = useContactsCached(userData?._id);
  const notifications = useHubNotifications({
    onInvitationAccepted: () => {
      refreshContacts?.();
      setEngagementRefreshKey((k) => k + 1);
    },
  });

  const eventPrefsSet = useMemo(() => hasEventPreferences(preferences), [preferences]);
  const newsPrefsSet = useMemo(
    () => hasNewsPreferences(preferences, { watchlistCount }),
    [preferences, watchlistCount]
  );
  const mostRecentContacts = useMemo(() => getMostRecentContacts(contacts, 3), [contacts]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoadingPrefs(true);

    (async () => {
      try {
        const res = await api.get('/api/user/insight-preferences', { signal: controller.signal });
        if (controller.signal.aborted) return;
        const prefs = res.data?.preferences || null;
        setWatchlistCount(Number(res.data?.watchlistCount) || 0);
        if (prefs?.locationPreferences?.followCurrentMarket) {
          await refreshCurrentMarketIfNeeded({ preferences: prefs, force: false });
        }
        if (controller.signal.aborted) return;
        setPreferences(prefs);
      } catch (e) {
        if (isAbortError(e) || controller.signal.aborted) return;
        setPreferences(null);
        setWatchlistCount(0);
      } finally {
        if (!controller.signal.aborted) setIsLoadingPrefs(false);
      }
    })();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoadingEngagement(true);

    (async () => {
      try {
        const res = await api.get('/api/user-analytics/engagement', {
          params: { range: 'all' },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setEngagementSummary(res.data?.summary || null);
      } catch (e) {
        if (isAbortError(e) || controller.signal.aborted) return;
        // TODO(Home hub): If engagement endpoints are unavailable in some envs, keep this null and show empty state.
        setEngagementSummary(null);
      } finally {
        if (!controller.signal.aborted) setIsLoadingEngagement(false);
      }
    })();

    return () => controller.abort();
  }, [engagementRefreshKey]);

  useEffect(() => {
    if (isLoadingPrefs || entitlementsLoading) return undefined;

    if (!newsPrefsSet && !eventPrefsSet) {
      setEventsExtract([]);
      setNewsExtract([]);
      setEventsLocked(!canSeeEvents);
      setIsLoadingExtracts(false);
      return undefined;
    }

    if (!canSeeEvents) {
      setEventsExtract([]);
      setEventsLocked(true);
    } else if (eventPrefsSet) {
      setEventsLocked(false);
    }

    const willFetchEvents = eventPrefsSet && canSeeEvents;
    const willFetchNews = newsPrefsSet;
    if (!willFetchEvents && !willFetchNews) {
      setIsLoadingExtracts(false);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoadingExtracts(true);
    const cancelIdle = requestIdle(async () => {
      if (controller.signal.aborted) return;
      try {
        const [eventsResult, newsResult] = await Promise.allSettled([
          eventPrefsSet && canSeeEvents
            ? api.post(
                '/api/insights/events-summary',
                { preferences },
                { signal: controller.signal }
              )
            : Promise.resolve(null),
          newsPrefsSet
            ? api.get('/api/insights/v2/news/teaser', { signal: controller.signal })
            : Promise.resolve(null),
        ]);

        if (controller.signal.aborted) return;

        if (!canSeeEvents) {
          setEventsExtract([]);
          setEventsLocked(true);
        } else if (!eventPrefsSet) {
          setEventsExtract([]);
          setEventsLocked(false);
        } else if (eventsResult.status === 'fulfilled') {
          const eventsPayload = eventsResult.value?.data;
          setEventsExtract(
            Array.isArray(eventsPayload?.events)
              ? eventsPayload.events
              : Array.isArray(eventsPayload)
                ? eventsPayload
                : []
          );
          setEventsLocked(false);
        } else if (!isAbortError(eventsResult.reason)) {
          setEventsExtract(null);
          setEventsLocked(eventsResult.reason?.response?.status === 402);
        }

        if (!newsPrefsSet) {
          setNewsExtract([]);
        } else if (newsResult.status === 'fulfilled') {
          const newsPayload = newsResult.value?.data;
          setNewsExtract(Array.isArray(newsPayload?.teaser) ? newsPayload.teaser : []);
          setNewsTeaserStatus(newsPayload?.status || null);
          setNewsMeta({
            isStale: newsPayload?.isStale,
            refreshPending: newsPayload?.refreshPending,
            lastCorpusRefreshAt: newsPayload?.lastCorpusRefreshAt
          });
        } else if (!isAbortError(newsResult.reason)) {
          setNewsExtract(null);
        }
      } catch (e) {
        if (isAbortError(e) || controller.signal.aborted) return;
        setEventsExtract(null);
        setNewsExtract(null);
      } finally {
        if (!controller.signal.aborted) setIsLoadingExtracts(false);
      }
    });

    return () => {
      cancelIdle?.();
      controller.abort();
    };
  }, [newsPrefsSet, eventPrefsSet, preferences, isLoadingPrefs, canSeeEvents, entitlementsLoading]);

  const kpis = useMemo(() => {
    const totalContacts = Array.isArray(contacts) ? contacts.length : null;
    const flaggedContacts =
      Array.isArray(contacts) ? contacts.filter((c) => c && c.isFlagged).length : null;

    const cardsShared = engagementSummary?.totalNfcScans ?? null;

    const contactsLoading = isLoadingAccount || isLoadingContacts;
    const engagementLoading = isLoadingEngagement;

    return [
      {
        label: 'Total Contacts',
        value: contactsLoading ? '__loading__' : totalContacts,
        to: '/contacts',
        ariaLabel: 'Total contacts: open contacts',
      },
      {
        label: 'Cards Shared',
        value: engagementLoading ? '__loading__' : cardsShared,
        helper: 'All time',
        emptyLabel: 'Not available yet',
        to: '/advanced-analytics#cards-shared',
        ariaLabel: 'Cards shared: open analytics',
      },
      {
        label: 'Flagged',
        value: contactsLoading ? '__loading__' : flaggedContacts,
        to: '/contacts?filter=flagged',
        ariaLabel: 'Flagged contacts: open contacts filtered by flagged',
      },
    ];
  }, [contacts, engagementSummary, isLoadingAccount, isLoadingContacts, isLoadingEngagement]);

  const topEvents = useMemo(() => selectUpcomingEvents(eventsExtract), [eventsExtract]);

  const topNews = useMemo(
    () => selectHomeTeasers(newsExtract, { full: canSeeBriefing }),
    [newsExtract, canSeeBriefing]
  );
  const briefingInProgress = isBriefingInProgress(newsMeta, newsTeaserStatus);

  const handleGoToContacts = () => {
    navigate('/contacts');
  };

  const showAnyLoadingKpis =
    isLoadingAccount ||
    isLoadingContacts ||
    isLoadingPrefs ||
    isLoadingEngagement;

  return (
    <div className={styles.hubRoot}>
      <HomeSidebar
        open={isNavOpen}
        onClose={() => setIsNavOpen(false)}
        activePath={location.pathname}
        onNavigate={(to) => {
          setIsNavOpen(false);
          navigate(to);
        }}
      />

      <NotificationsDrawer
        open={notifications.open}
        onClose={notifications.close}
        {...notifications.drawerProps}
      />

      <div className={styles.hubMain}>
        <HomeTopBar
          title="Home"
          onMenu={() => setIsNavOpen(true)}
          notificationsBadge={notifications.badge}
          onNotifications={notifications.toggle}
        />

        <div className={styles.hubContent}>
          <GuestMergeNoticeBanner />
          <section className={styles.kpiSection} aria-label="Key performance indicators">
            <div className={styles.kpiHeaderRow}>
              <div className={styles.kpiHeader}>
                <div className={styles.kpiEyebrow}>{todayLabel}</div>
                <div className={styles.kpiTitle}>Your network at a glance</div>
              </div>
              <div className={styles.kpiStatus}>
                {showAnyLoadingKpis ? <span className={styles.kpiLoading}>Updating…</span> : null}
              </div>
            </div>

            <div className={styles.kpiGrid}>
              {kpis.map((kpi) => (
                <KpiCard
                  key={kpi.label}
                  label={kpi.label}
                  value={kpi.value}
                  helper={kpi.helper}
                  emptyLabel={kpi.emptyLabel}
                  to={kpi.to}
                  ariaLabel={kpi.ariaLabel}
                />
              ))}
            </div>
          </section>

          <section className={styles.modulesGrid} aria-label="Home modules">{/*
            <ModuleCard
              title="Notifications"
              subtitle="Invitations and account actions"
              actionLabel={isLoadingInvites ? 'Loading…' : 'Open'}
              actionDisabled={isLoadingInvites}
              onAction={() => navigate('/invitations')}
            >
              <div className={styles.notificationsBody}>
                {isLoadingInvites ? (
                  <div className={styles.noticeRow} aria-hidden="true">
                    <div className={styles.noticeTitle}>
                      <span className={styles.skeleton} style={{ width: 86, height: 12, borderRadius: 10, display: 'block' }} />
                    </div>
                    <div className={styles.noticeValue}>
                      <span className={styles.skeleton} style={{ width: 22, height: 14, borderRadius: 10, display: 'block' }} />
                    </div>
                  </div>
                ) : Array.isArray(invitations) && invitations.length > 0 ? (
                  <div className={styles.noticeRow}>
                    <div className={styles.noticeTitle}>Invitations</div>
                    <div className={styles.noticeValue}>
                      <span className={styles.goldNumber}>{invitations.length}</span>
                    </div>
                  </div>
                ) : (
                  <div className={styles.noticeRow}>
                    <div className={styles.noticeTitle}>Invitations</div>
                    <div className={styles.noticeValue}>None pending</div>
                  </div>
                )}

                <div className={styles.noticeRow}>
                  <div className={styles.noticeTitle}>Account</div>
                  <div className={styles.noticeValue}>
                    {isLoadingAccount ? (
                      <span className={styles.muted}>Checking…</span>
                    ) : needsAccountAction ? (
                      <button
                        type="button"
                        className={styles.inlineLink}
                        onClick={() => navigate('/account')}
                      >
                        Link your card
                      </button>
                    ) : (
                      'All set'
                    )}
                  </div>
                </div>
              </div>
            </ModuleCard>
*/}
            <ModuleCard
              className={styles.moduleOrderContacts}
              title="Contacts"
              subtitle="Your most recent contact"
            >
              {/* An unresolved list is not an empty one: without this branch the
                  module claims "No contacts yet" for the whole fetch. */}
              {isLoadingAccount || isLoadingContacts ? (
                <CardStackSkeleton />
              ) : mostRecentContacts.length === 0 ? (
                <EmptyState
                  title="No contacts yet"
                  body="Contacts appear here when you save someone's Velox card, or when you accept an invitation."
                  ctaLabel="Go to Contacts"
                  onCta={handleGoToContacts}
                />
              ) : (
                <LastSavedCardStack
                  contacts={mostRecentContacts}
                  onOpenContacts={handleGoToContacts}
                />
              )}
            </ModuleCard>

            <ModuleCard
              className={styles.moduleOrderEvents}
              title="Networking Opportunities"
              subtitle="Relevant events and opportunities to connect"
            >
              {isLoadingPrefs || entitlementsLoading ? (
                <ModuleSkeletonList rows={2} />
              ) : !eventPrefsSet ? (
                <EmptyState
                  title="Find your next opportunity to connect"
                  body="Set your interests and event location to focus on conferences and meetups relevant to you."
                  ctaLabel="Set preferences"
                  onCta={() => navigate('/insight-preferences')}
                />
              ) : !canSeeEvents ? (
                <EmptyState
                  title="Discover events worth your time"
                  body="Explore conferences and meetups aligned with your industries and interests, and find new opportunities to connect."
                  ctaLabel="Open Networking"
                  onCta={() => navigate('/insights/events')}
                />
              ) : isLoadingExtracts ? (
                <ModuleSkeletonList rows={2} />
              ) : eventsLocked ? (
                <EmptyState
                  title="Discover events worth your time"
                  body="Explore conferences and meetups aligned with your industries and interests, and find new opportunities to connect."
                  ctaLabel="Open Networking"
                  onCta={() => navigate('/insights/events')}
                />
              ) : topEvents === null ? (
                <EmptyState
                  title="Not available right now"
                  body="Networking could not be loaded. Open it to try again."
                  ctaLabel="Open Networking"
                  onCta={() => navigate('/insights/events')}
                />
              ) : topEvents.length === 0 ? (
                <EmptyState
                  title="No upcoming matches found"
                  body="Adjust your interests or event location to focus on conferences and meetups that fit you."
                  ctaLabel="Update preferences"
                  onCta={() => navigate('/insight-preferences')}
                />
              ) : (
                <div className={styles.list}>
                  {topEvents.map((event, idx) => (
                    <Link
                      key={`${event.title || 'event'}-${idx}`}
                      className={styles.listRowButton}
                      to="/insights/events"
                    >
                      <div className={styles.listRowMain}>
                        <div className={styles.listRowTitle}>{event.title}</div>
                        <div className={styles.listRowMeta}>
                          {event.location ? <span>{event.location}</span> : null}
                          {event.location && (event.date || event.startDate) ? (
                            <span className={styles.dot} />
                          ) : null}
                          {event.date || event.startDate ? (
                            <span>
                              {new Date(event.date || event.startDate).toLocaleDateString(undefined, {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <span className={styles.srOnly}>Open Networking</span>
                      <div className={styles.listRowChevron} aria-hidden="true">
                        →
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </ModuleCard>

            <ModuleCard
              className={styles.moduleOrderNews}
              title="Market Intelligence"
              subtitle="The developments shaping your industries"
            >
              {isLoadingPrefs || entitlementsLoading ? (
                <ModuleSkeletonNews />
              ) : !newsPrefsSet ? (
                <EmptyState
                  title="Understand the moves shaping your market"
                  body="Choose the industries, topics and markets you follow to tailor your market analysis."
                  ctaLabel="Set preferences"
                  onCta={() => navigate('/insight-preferences')}
                />
              ) : isLoadingExtracts ? (
                <ModuleSkeletonNews />
              ) : topNews === null ? (
                <EmptyState
                  title="Not available right now"
                  body="Market Intelligence could not be loaded. Open it to try again."
                  ctaLabel="Open Market Intelligence"
                  onCta={() => navigate('/insights/news')}
                />
              ) : topNews.length === 0 ? (
                canSeeBriefing && briefingInProgress ? (
                  <EmptyState
                    title="Preparing your briefing"
                    body="A new edition is being written from your interests."
                    ctaLabel="Open Market Intelligence"
                    onCta={() => navigate('/insights/news')}
                  />
                ) : canSeeBriefing ? (
                  <EmptyState
                    title="No briefing yet"
                    body="Open Market Intelligence to prepare the first edition."
                    ctaLabel="Open Market Intelligence"
                    onCta={() => navigate('/insights/news')}
                  />
                ) : (
                  <EmptyState
                    title="See what matters in your market"
                    body="Explore market analysis and industry trends tailored to your interests, with sources behind each insight."
                    ctaLabel="Open Market Intelligence"
                    onCta={() => navigate('/insights/news')}
                  />
                )
              ) : (
                <div className={styles.newsStack} role="group" aria-label="Market Intelligence">
                  {topNews.map((item, idx) => (
                    <Link
                      key={item.id || idx}
                      className={styles.newsCard}
                      to="/insights/news"
                      onClick={() => {
                        trackNewsV2Event({
                          event: 'teaser_click',
                          targetType: 'teaser',
                          targetId: String(item.id || idx)
                        });
                      }}
                    >
                      <div className={styles.newsCardBody}>
                        <div className={styles.newsMeta}>
                          <span className={styles.newsPill}>{getNewsHeadlineTag(item)}</span>
                        </div>
                        <div className={styles.newsTitle}>{item.veloxHeadline}</div>
                        <div className={styles.newsSummary}>{item.oneLiner}</div>
                      </div>
                      <span className={styles.srOnly}>Open Market Intelligence</span>
                      <div className={styles.listRowChevron} aria-hidden="true">
                        →
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </ModuleCard>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HomePage;